import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  orcamentos as api,
  type DetalheDoOrcamento,
  type FiltroDeOrcamentos,
  type LinhaDaMemoria,
  type PaginaDoAdministrador,
} from '../api/orcamentos'
import { ErroApi } from '../api/client'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { FreteDoOrcamento } from './FreteDoOrcamento'
import { formatar } from '../i18n'
import './usuarios.css'
import './simulacao.css'
import './orcamentos.css'
import { marcaDaRota } from './marcaDaRota'


/** Quantas linhas por página. O servidor aceita até 200; 50 cabe numa rolagem. */
const TAMANHO = 50

/** O dia do calendário de quem está na tela, como `aaaa-mm-dd` — sem passar por UTC. */
const diaDe = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

type Atalho = 'hoje' | 'seteDias' | 'esteMes'

const ATALHOS: Atalho[] = ['hoje', 'seteDias', 'esteMes']

function periodoDo(atalho: Atalho, agora = new Date()): { de: string; ate: string } {
  const ate = diaDe(agora)

  if (atalho === 'hoje') return { de: ate, ate }

  if (atalho === 'esteMes') return { de: diaDe(new Date(agora.getFullYear(), agora.getMonth(), 1)), ate }

  // "7 dias" conta hoje: hoje e os seis dias anteriores.
  return { de: diaDe(new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 6)), ate }
}

/**
 * O administrador de orçamentos.
 *
 * Responde "o que foi orçado neste período, para quem, e onde está a conversa" sem
 * abrir orçamento por orçamento: período, produto, assistente e busca em cima; em cada
 * linha, o que foi orçado, o contato e os links para a conversa e para o contato.
 *
 * O detalhe continua o mesmo: reproduz o cálculo com a versão que o orçamento guardou —
 * é o que responde "por que este orçamento saiu assim em julho" sem depender da memória
 * de ninguém.
 */
export function Orcamentos() {
  const { t } = useTranslation()
  const marca = (marcaDaRota() ?? '')

  // A tela abre nos últimos sete dias: é a pergunta do dia a dia, e evita carregar
  // o histórico inteiro para quem só quer ver a semana.
  const [filtro, setFiltro] = useState<FiltroDeOrcamentos>(() => periodoDo('seteDias'))
  const [pagina, setPagina] = useState(1)
  const [dados, setDados] = useState<PaginaDoAdministrador | null>(null)
  const [atualizando, setAtualizando] = useState(false)
  const [aberto, setAberto] = useState<string | null>(null)
  const [falhou, setFalhou] = useState(false)

  // O que o servidor disse quando recusou o filtro (período inválido, por exemplo).
  const [recusa, setRecusa] = useState<string | null>(null)
  const [exportando, setExportando] = useState(false)
  const [exportacaoFalhou, setExportacaoFalhou] = useState(false)

  // Só a resposta do pedido mais recente vale: trocar o filtro duas vezes seguidas
  // não pode deixar a resposta atrasada da primeira por cima da segunda.
  const pedido = useRef(0)

  const carregar = useCallback(async () => {
    const meu = ++pedido.current

    setFalhou(false)
    setRecusa(null)
    setAtualizando(true)

    try {
      const resposta = await api.administrar(marca, filtro, pagina, TAMANHO)

      if (meu === pedido.current) setDados(resposta)
    } catch (e) {
      if (meu !== pedido.current) return

      setFalhou(true)

      // 400 é o servidor recusando o FILTRO, e ele diz por quê. Mostrar "tente de novo" para
      // um período inválido faria a pessoa repetir o mesmo pedido para sempre.
      const motivo = e instanceof ErroApi && e.status === 400 ? (e.corpo as { erro?: unknown } | null)?.erro : null

      setRecusa(typeof motivo === 'string' && motivo.trim() ? motivo : null)
    } finally {
      if (meu === pedido.current) setAtualizando(false)
    }
  }, [marca, filtro, pagina])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const alterar = (parcial: Partial<FiltroDeOrcamentos>) => {
    setPagina(1)
    setFiltro((atual) => {
      const novo = { ...atual, ...parcial }

      // Período invertido não existe: a ponta que não foi mexida acompanha a outra.
      if (novo.de && novo.ate && novo.de > novo.ate) {
        if ('de' in parcial) novo.ate = novo.de
        else novo.de = novo.ate
      }

      return novo
    })
  }

  const limpar = () => {
    setPagina(1)
    setFiltro({})
  }

  const exportar = async () => {
    setExportando(true)
    setExportacaoFalhou(false)

    try {
      const { arquivo, nome } = await api.exportar(marca, filtro)
      const endereco = URL.createObjectURL(arquivo)
      const link = document.createElement('a')

      link.href = endereco
      link.download = nome
      document.body.append(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(endereco)
    } catch {
      setExportacaoFalhou(true)
    } finally {
      setExportando(false)
    }
  }

  if (aberto)
    return (
      <Detalhe
        marca={marca}
        numero={aberto}
        aoFechar={() => {
          setAberto(null)

          // O que se faz no detalhe (frete escolhido hoje; exclusão quando a tela tiver)
          // muda a linha: ao voltar, a lista é lida de novo em vez de mostrar o que era.
          void carregar()
        }}
      />
    )

  // Só na primeira carga, antes de haver qualquer coisa para mostrar. Depois dela os
  // filtros ficam sempre na tela — inclusive quando o pedido falha.
  if (!dados && !falhou) return <Estado tipo="carregando" />

  const linhas = dados?.linhas ?? []
  const total = dados?.total ?? 0
  const quantidade = dados?.quantidade ?? 0
  const soma = dados?.soma ?? 0
  const paginas = Math.max(1, Math.ceil(total / (dados?.tamanho ?? TAMANHO)))
  const filtrado = Object.values(filtro).some((v) => v !== undefined && v !== '')

  return (
    <main className="usuarios orcamentos">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('orcamentos.titulo')}</h1>
          <p className="usuarios__descricao">{t('orcamentos.descricaoDaLista')}</p>
        </div>

        <div className="usuarios__acoes-linha">
          <Botao onClick={exportar} carregando={exportando} disabled={falhou || total === 0}>
            {t('orcamentos.exportar')}
          </Botao>
          <Botao onClick={() => (window.location.href = `/marcas/${marca}/simulacao`)}>
            {t('orcamentos.simular')}
          </Botao>
        </div>
      </header>

      <Filtros
        filtro={filtro}
        familias={dados?.familias ?? []}
        assistentes={dados?.assistentes ?? []}
        alterar={alterar}
        limpar={filtrado ? limpar : undefined}
      />

      {exportacaoFalhou && <Aviso tom="perigo">{t('orcamentos.exportacaoFalhou')}</Aviso>}

      {!falhou && dados && total > dados.tetoDaExportacao && (
        <Aviso tom="info">
          {t('orcamentos.exportacaoParcial', { teto: formatar.numero(dados.tetoDaExportacao) })}
        </Aviso>
      )}

      {/* 🔴 O erro fica NO LUGAR DA TABELA, com os filtros acima dele: quem errou o período
          conserta ali mesmo, sem recarregar a página. */}
      {falhou ? (
        <Estado
          tipo="erro"
          titulo={recusa ? t('orcamentos.filtroRecusado') : undefined}
          descricao={recusa ?? undefined}
          acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>}
        />
      ) : linhas.length === 0 ? (
        <Estado
          tipo="vazio"
          descricao={t(filtrado ? 'orcamentos.vazioFiltrado' : 'orcamentos.vazio')}
        />
      ) : (
        <div
          className="usuarios__tabela-rolagem"
          // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
          // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
          tabIndex={0}
          role="group"
          aria-label={t('orcamentos.legenda')}
          aria-busy={atualizando || undefined}
        >
          <table className="usuarios__tabela orcamentos__tabela">
            <caption className="usuarios__legenda">{t('orcamentos.legenda')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('orcamentos.quando')}</th>
                <th scope="col">{t('orcamentos.produto')}</th>
                <th scope="col">{t('orcamentos.resumo')}</th>
                <th scope="col">{t('orcamentos.contato')}</th>
                <th scope="col">{t('orcamentos.canal')}</th>
                <th scope="col">{t('orcamentos.numero')}</th>
                <th scope="col">{t('orcamentos.referencia')}</th>
                <th scope="col" className="orcamentos__valor">{t('simulacao.tiragem')}</th>
                <th scope="col" className="orcamentos__valor">{t('simulacao.total')}</th>
                <th scope="col" className="orcamentos__valor">{t('simulacao.unitario')}</th>
                <th scope="col">{t('usuarios.acoes')}</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((o) => (
                <tr key={o.numero} className={o.excluido ? 'orcamentos__linha--excluida' : undefined}>
                  <td className="orcamentos__quando">{formatar.dataHora(o.calculadoEm)}</td>
                  <td className="orcamentos__texto">
                    {o.familia}
                    {/* O selo diz em TEXTO o que é; a cor só reforça (UI-09). */}
                    {o.repetido && (
                      <span
                        className="etiqueta etiqueta--pendente orcamentos__selo"
                        title={t('orcamentos.repetidoAjuda')}
                      >
                        {t('orcamentos.repetido')}
                      </span>
                    )}
                    {o.excluido && (
                      <span className="etiqueta etiqueta--inativo orcamentos__selo">
                        {t('orcamentos.excluidoSelo')}
                      </span>
                    )}
                  </td>
                  <td className="orcamentos__resumo">{o.resumo ?? '—'}</td>
                  <td className="orcamentos__texto">
                    {o.contato ?? (o.contatoSemNome ? t('orcamentos.semNomeCadastrado') : '—')}
                    {o.linkDoContato && (
                      <a
                        className="orcamentos__link"
                        href={o.linkDoContato}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={
                          o.contato
                            ? t('orcamentos.abrirContatoDe', { numero: o.numero, contato: o.contato })
                            : t('orcamentos.abrirContatoSemNomeDe', { numero: o.numero })
                        }
                      >
                        {t('orcamentos.abrirContato')}
                      </a>
                    )}
                  </td>
                  <td className="orcamentos__texto">
                    {o.canal ?? '—'}
                    {o.assistente && <span className="orcamentos__detalhe">{o.assistente}</span>}
                    {o.linkDaConversa && (
                      <a
                        className="orcamentos__link"
                        href={o.linkDaConversa}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('orcamentos.abrirConversaDe', { numero: o.numero, conversa: o.conversa })}
                      >
                        {t('orcamentos.abrirConversa')}
                      </a>
                    )}
                  </td>
                  <th scope="row" className="orcamentos__numero">{o.numero}</th>
                  {/* A referência da planilha é o que o painel do Chatwoot mostra (292):
                      quem atende chega com ela, e acha aqui o orçamento auditável. */}
                  <td className="orcamentos__texto">{o.referencia?.trim() || t('orcamentos.semReferencia')}</td>
                  <td className="orcamentos__valor">{formatar.numero(o.tiragem)}</td>
                  <td className="orcamentos__valor">{formatar.moeda(o.total)}</td>
                  <td className="orcamentos__valor">
                    {formatar.numero(o.unitario, {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4,
                    })}
                  </td>
                  <td>
                    <Botao variante="discreto" onClick={() => setAberto(o.numero)}>
                      {t('orcamentos.abrir')}
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!falhou && (
      <footer className="orcamentos__rodape">
        {/* `status`: quem usa leitor de tela ouve o novo total ao trocar o filtro. */}
        <p className="orcamentos__totais" role="status">
          <strong>{t('orcamentos.quantidade', { count: quantidade })}</strong>
          <span>{t('orcamentos.soma', { soma: formatar.moeda(soma) })}</span>
          {total > quantidade && (
            <span className="orcamentos__detalhe">
              {t('orcamentos.foraDaSoma', { count: total - quantidade })}
            </span>
          )}
        </p>

        {paginas > 1 && (
          <nav className="usuarios__acoes-linha" aria-label={t('orcamentos.paginacao')}>
            <Botao variante="discreto" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
              {t('orcamentos.anterior')}
            </Botao>
            <span className="orcamentos__pagina">{t('orcamentos.pagina', { pagina, paginas })}</span>
            <Botao variante="discreto" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)}>
              {t('orcamentos.proxima')}
            </Botao>
          </nav>
        )}
      </footer>
      )}
    </main>
  )
}

function Filtros({
  filtro,
  familias,
  assistentes,
  alterar,
  limpar,
}: {
  filtro: FiltroDeOrcamentos
  familias: PaginaDoAdministrador['familias']
  assistentes: PaginaDoAdministrador['assistentes']
  alterar: (parcial: Partial<FiltroDeOrcamentos>) => void
  limpar?: () => void
}) {
  const { t } = useTranslation()

  // A busca só vale ao sair do campo ou com Enter: filtrar a cada tecla dispararia
  // uma consulta por letra digitada.
  const [busca, setBusca] = useState(filtro.busca ?? '')

  useEffect(() => setBusca(filtro.busca ?? ''), [filtro.busca])

  const aplicarBusca = () => {
    const texto = busca.trim() || undefined

    if (texto !== filtro.busca) alterar({ busca: texto })
  }

  return (
    <section className="orcamentos__filtros" aria-label={t('orcamentos.filtros')}>
      <div className="usuarios__acoes-linha" role="group" aria-label={t('orcamentos.periodo')}>
        {ATALHOS.map((atalho) => {
          const periodo = periodoDo(atalho)

          return (
            <button
              key={atalho}
              type="button"
              className="orcamentos__atalho"
              aria-pressed={filtro.de === periodo.de && filtro.ate === periodo.ate}
              onClick={() => alterar(periodo)}
            >
              {t(`orcamentos.atalho.${atalho}`)}
            </button>
          )
        })}
      </div>

      {/* Rótulo por `htmlFor`, e não envolvendo o campo: um `<label>` que envolve o
          `<select>` põe a opção escolhida dentro do nome que o leitor de tela lê. */}
      <div className="orcamentos__campos">
        <CampoDeFiltro rotulo={t('orcamentos.de')}>
          {(id) => (
            <input
              id={id}
              type="date"
              className="campo__entrada"
              value={filtro.de ?? ''}
              onChange={(e) => alterar({ de: e.target.value || undefined })}
            />
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('orcamentos.ate')}>
          {(id) => (
            <input
              id={id}
              type="date"
              className="campo__entrada"
              value={filtro.ate ?? ''}
              onChange={(e) => alterar({ ate: e.target.value || undefined })}
            />
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('orcamentos.produto')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.familia ?? ''}
              onChange={(e) => alterar({ familia: e.target.value || undefined })}
            >
              <option value="">{t('orcamentos.todos')}</option>
              {familias.map((familia) => (
                <option key={familia} value={familia}>
                  {familia}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('orcamentos.assistente')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.assistenteId ?? ''}
              onChange={(e) => alterar({ assistenteId: e.target.value ? Number(e.target.value) : undefined })}
            >
              <option value="">{t('orcamentos.todos')}</option>
              {assistentes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('orcamentos.busca')}>
          {(id) => (
            <input
              id={id}
              type="search"
              className="campo__entrada"
              placeholder={t('orcamentos.buscaExemplo')}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onBlur={aplicarBusca}
              onKeyDown={(e) => {
                if (e.key === 'Enter') aplicarBusca()
              }}
            />
          )}
        </CampoDeFiltro>

        {limpar && (
          <Botao variante="discreto" onClick={limpar}>
            {t('orcamentos.limpar')}
          </Botao>
        )}
      </div>
    </section>
  )
}

function CampoDeFiltro({ rotulo, children }: { rotulo: string; children: (id: string) => ReactNode }) {
  const id = useId()

  return (
    <div className="orcamentos__campo">
      <label htmlFor={id}>{rotulo}</label>
      {children(id)}
    </div>
  )
}

function Detalhe({
  marca,
  numero,
  aoFechar,
}: {
  marca: string
  numero: string
  aoFechar: () => void
}) {
  const { t } = useTranslation()

  const [dados, setDados] = useState<DetalheDoOrcamento | null>(null)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    api
      .detalhar(marca, numero)
      .then(setDados)
      .catch(() => setFalhou(true))
  }, [marca, numero])

  if (falhou) return <Estado tipo="erro" />
  if (!dados) return <Estado tipo="carregando" />

  const memoria: LinhaDaMemoria[] = JSON.parse(dados.memoria)
  const entrada: Record<string, unknown> = JSON.parse(dados.entrada)

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{dados.numero}</h1>
          <p className="usuarios__descricao">
            {t('orcamentos.calculadoEm', {
              quando: formatar.dataHora(dados.calculadoEm),
              versao: dados.versao,
            })}
          </p>
        </div>

        <Botao variante="discreto" onClick={aoFechar}>
          {t('orcamentos.voltar')}
        </Botao>
      </header>

      {dados.excluido && <Aviso tom="alerta">{t('orcamentos.excluido')}</Aviso>}

      {/* Se isto vier falso, alguma coisa mudou que não deveria ter mudado —
          e é melhor descobrir aqui do que diante do cliente. */}
      {dados.reproduzOValorOriginal ? (
        <Aviso tom="sucesso">{t('orcamentos.reproduz')}</Aviso>
      ) : (
        <Aviso tom="perigo" titulo={t('orcamentos.naoReproduzTitulo')}>
          {t('orcamentos.naoReproduz')}
        </Aviso>
      )}

      {/* Antes da entrada e da memória de propósito: é o que muda hoje. A
          memória é conferência; o frete é decisão em aberto. */}
      <FreteDoOrcamento marca={marca} orcamento={dados} />

      <Cartao titulo={t('orcamentos.entrada')}>
        <dl className="simulacao__entrada">
          {Object.entries(entrada).map(([chave, valor]) => (
            <div key={chave}>
              <dt>{chave}</dt>
              <dd>{valor === null ? '—' : String(valor)}</dd>
            </div>
          ))}
        </dl>
      </Cartao>

      <Cartao titulo={t('simulacao.memoria')}>
        <div
          className="usuarios__tabela-rolagem"
          // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1).
          tabIndex={0}
          role="group"
          aria-label={t('simulacao.memoriaLegenda')}
        >
          <table className="usuarios__tabela simulacao__memoria">
            <caption className="usuarios__legenda">{t('simulacao.memoriaLegenda')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('simulacao.etapa')}</th>
                <th scope="col">{t('simulacao.linha')}</th>
                <th scope="col">{t('simulacao.valor')}</th>
              </tr>
            </thead>
            <tbody>
              {memoria.map((linha, i) => (
                <tr key={i} className={linha.etapa === 'Resultado' ? 'simulacao__final' : undefined}>
                  <td>{linha.etapa}</td>
                  <td>{linha.descricao}</td>
                  <td className="simulacao__numero">
                    {formatar.numero(linha.valor, { maximumFractionDigits: 4 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Cartao>
    </main>
  )
}
